import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

interface PlasticityType {
  name: string;
  description: string;
  icon: string;
  color: string;
}

interface BrainRegion {
  name: string;
  function: string;
  icon: string;
  color: string;
}

interface EnhancementMethod {
  name: string;
  effect: string;
  duration: string;
  icon: string;
}

interface BrainFact {
  label: string;
  icon: string;
}

interface BrainPlasticityTemplateProps {
  topic: string;
  subtitle: string;
  plasticityTypes: PlasticityType[];
  brainRegions: BrainRegion[];
  enhancementMethods: EnhancementMethod[];
  brainFacts: BrainFact[];
  neuralNodes: { x: number; y: number; label: string }[];
  stats: { value: string; label: string }[];
  bottomQuote: string;
}

const DEFAULT_PROPS: BrainPlasticityTemplateProps = {
  topic: '大脑可塑性与神经重塑',
  subtitle: 'Brain Plasticity & Neural Remodeling',
  plasticityTypes: [
    {
      name: '突触可塑性',
      description: '突触连接强度的动态调整',
      icon: '🔗',
      color: '#f43f5e',
    },
    {
      name: '结构可塑性',
      description: '神经元形态与突触数量改变',
      icon: '🏗️',
      color: '#fb7185',
    },
    {
      name: '功能可塑性',
      description: '脑区功能的重新分配与代偿',
      icon: '🔄',
      color: '#fda4af',
    },
    {
      name: '跨模态可塑性',
      description: '一种感觉皮层接管另一种功能',
      icon: '🌐',
      color: '#fecdd3',
    },
  ],
  brainRegions: [
    {
      name: '前额叶',
      function: '决策、规划与高级认知',
      icon: '🧠',
      color: '#f43f5e',
    },
    {
      name: '海马体',
      function: '记忆形成与空间导航',
      icon: '🐴',
      color: '#fb7185',
    },
    {
      name: '杏仁核',
      function: '情绪处理与恐惧反应',
      icon: '💜',
      color: '#fda4af',
    },
    {
      name: '小脑',
      function: '运动协调与动作学习',
      icon: '🎯',
      color: '#fecdd3',
    },
  ],
  enhancementMethods: [
    {
      name: '有氧运动',
      effect: '促进BDNF分泌、增加海马体积',
      duration: '每周150分钟',
      icon: '🏃',
    },
    {
      name: '冥想练习',
      effect: '增厚前额叶皮层、降低杏仁核活跃度',
      duration: '每天20分钟',
      icon: '🧘',
    },
    {
      name: '学习新技能',
      effect: '建立新突触连接、增强白质完整性',
      duration: '持续学习',
      icon: '📚',
    },
    {
      name: '充足睡眠',
      effect: '突触修剪与记忆巩固',
      duration: '7-9小时/晚',
      icon: '😴',
    },
  ],
  brainFacts: [
    { label: 'BDNF因子', icon: '🧬' },
    { label: '长时程增强', icon: '⚡' },
    { label: '髓鞘化', icon: '🛡️' },
    { label: '神经发生', icon: '🌱' },
  ],
  neuralNodes: [
    { x: 150, y: 140, label: '视觉皮层' },
    { x: 350, y: 80, label: '运动皮层' },
    { x: 550, y: 120, label: '听觉皮层' },
    { x: 250, y: 260, label: '海马体' },
    { x: 450, y: 245, label: '前额叶' },
    { x: 350, y: 190, label: '丘脑' },
  ],
  stats: [
    { value: '860亿', label: '人脑神经元数量' },
    { value: '100万亿', label: '突触连接总数' },
    { value: '25岁', label: '前额叶完全发育年龄' },
  ],
  bottomQuote:
    '大脑并非固定不变的器官——它终其一生都在重塑自己，每一次学习和经历都在改写神经网络的连接图谱',
};

export const BrainPlasticityTemplate: React.FC<
  Partial<BrainPlasticityTemplateProps>
> = (props) => {
  const p = { ...DEFAULT_PROPS, ...props };
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const headerIn = spring({
    frame,
    fps,
    config: { damping: 18, stiffness: 80 },
  });

  const plasticityStart = 30;
  const regionStart = 160;
  const svgStart = 200;
  const methodStart = 300;
  const factStart = 380;
  const statsStart = 440;
  const quoteStart = 500;

  const quoteOpacity = interpolate(frame, [quoteStart, quoteStart + 30], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  const networkDraw = interpolate(
    frame,
    [svgStart, svgStart + 100],
    [0, 1],
    { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
  );

  return (
    <AbsoluteFill
      style={{
        background: 'linear-gradient(180deg, #3a0a1e 0%, #1c0612 50%, #0f030a 100%)',
        fontFamily: '-apple-system, "PingFang SC", "Noto Sans SC", sans-serif',
        color: 'white',
        overflow: 'hidden',
        width: 1920,
        height: 1080,
      }}
    >
      {/* Floating neuron-like particles with dendrites */}
      {Array.from({ length: 50 }).map((_, i) => {
        const x = (i * 313) % 1920;
        const y = (i * 181) % 1080;
        const size = 3 + (i % 6);
        const drift = Math.sin(frame * 0.02 + i) * 8;
        const pulse = 0.05 + 0.05 * Math.sin(frame * 0.04 + i * 0.5);
        const colors = ['#f43f5e', '#fb7185', '#fda4af', '#fecdd3'];
        const color = colors[i % colors.length];
        const dendriteLen = size * 2.5;
        const angles = [0, 72, 144, 216, 288];
        return (
          <div key={`p-${i}`} style={{ position: 'absolute', left: x, top: y + drift }}>
            <svg
              width={size * 6}
              height={size * 6}
              viewBox={`0 0 ${size * 6} ${size * 6}`}
              style={{ opacity: pulse }}
            >
              <circle
                cx={size * 3}
                cy={size * 3}
                r={size / 2}
                fill={color}
              />
              {angles.map((a, j) => {
                const rad = (a * Math.PI) / 180;
                return (
                  <line
                    key={j}
                    x1={size * 3}
                    y1={size * 3}
                    x2={size * 3 + dendriteLen * Math.cos(rad)}
                    y2={size * 3 + dendriteLen * Math.sin(rad)}
                    stroke={color}
                    strokeWidth="0.8"
                    opacity="0.6"
                  />
                );
              })}
            </svg>
          </div>
        );
      })}

      {/* ===== Header ===== */}
      <div
        style={{
          position: 'absolute',
          top: 28,
          left: 44,
          right: 44,
          opacity: headerIn,
          transform: `translateY(${(1 - headerIn) * 20}px)`,
          display: 'flex',
          alignItems: 'center',
          gap: 20,
        }}
      >
        <div
          style={{
            padding: '6px 18px',
            background: '#be123c',
            borderRadius: 8,
            fontSize: 24,
            fontWeight: 700,
            color: '#fecdd3',
            flexShrink: 0,
          }}
        >
          🧠 神经科学
        </div>
        <h1
          style={{
            fontSize: 46,
            fontWeight: 900,
            margin: 0,
            color: '#ffe4e6',
            whiteSpace: 'nowrap',
          }}
        >
          {p.topic}
        </h1>
        <p
          style={{
            fontSize: 26,
            color: '#94a3b8',
            margin: 0,
            whiteSpace: 'nowrap',
          }}
        >
          {p.subtitle}
        </p>
      </div>

      {/* ===== LEFT COLUMN: Plasticity Types + Brain Regions ===== */}
      <div style={{ position: 'absolute', top: 120, left: 36, width: 500 }}>
        <div
          style={{
            fontSize: 26,
            color: '#94a3b8',
            marginBottom: 10,
            letterSpacing: '0.08em',
          }}
        >
          可塑性类型
        </div>
        {p.plasticityTypes.map((pt, i) => {
          const s = spring({
            frame: frame - (plasticityStart + i * 16),
            fps,
            config: { damping: 14, stiffness: 120 },
          });
          return (
            <div
              key={i}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 14,
                background: '#2a0a18',
                borderRadius: 14,
                padding: '12px 16px',
                border: `1.5px solid ${pt.color}30`,
                marginBottom: 8,
                opacity: s,
                transform: `translateX(${(1 - s) * 30}px)`,
              }}
            >
              <div
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  background: `${pt.color}20`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 28,
                  flexShrink: 0,
                  boxShadow: `0 0 18px ${pt.color}30`,
                }}
              >
                {pt.icon}
              </div>
              <div style={{ flex: 1 }}>
                <span style={{ fontSize: 24, fontWeight: 800, color: pt.color }}>
                  {pt.name}
                </span>
                <div style={{ fontSize: 22, color: '#94a3b8', marginTop: 2 }}>
                  {pt.description}
                </div>
              </div>
            </div>
          );
        })}

        {/* Brain Regions */}
        <div style={{ marginTop: 16 }}>
          <div
            style={{
              fontSize: 26,
              color: '#94a3b8',
              marginBottom: 10,
              letterSpacing: '0.08em',
            }}
          >
            关键脑区
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 8,
            }}
          >
            {p.brainRegions.map((br, i) => {
              const s = spring({
                frame: frame - (regionStart + i * 14),
                fps,
                config: { damping: 14, stiffness: 120 },
              });
              return (
                <div
                  key={i}
                  style={{
                    background: '#2a0a18',
                    borderRadius: 12,
                    padding: '12px 10px',
                    border: `1px solid ${br.color}30`,
                    opacity: s,
                    transform: `scale(${0.8 + 0.2 * s})`,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                    <span style={{ fontSize: 26 }}>{br.icon}</span>
                    <span style={{ fontSize: 24, fontWeight: 700, color: br.color }}>{br.name}</span>
                  </div>
                  <div style={{ fontSize: 22, color: '#64748b', marginTop: 2 }}>
                    {br.function}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ===== CENTER: Neural Network SVG + Enhancement Methods ===== */}
      <div style={{ position: 'absolute', top: 120, left: 580, width: 750 }}>
        <div
          style={{
            fontSize: 26,
            color: '#94a3b8',
            marginBottom: -15,
            letterSpacing: '0.08em',
          }}
        >
          神经网络连接图
        </div>
        <svg width="700" height="360" viewBox="0 0 700 360">
          {/* Connection lines between nodes */}
          {p.neuralNodes.map((nodeA, i) =>
            p.neuralNodes.slice(i + 1).map((nodeB, j) => {
              const dist = Math.sqrt(
                (nodeA.x - nodeB.x) ** 2 + (nodeA.y - nodeB.y) ** 2,
              );
              if (dist > 300) return null;
              const lineDelay = svgStart + (i + j) * 8;
              const lineOp = interpolate(
                frame,
                [lineDelay, lineDelay + 30],
                [0, 0.3],
                { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
              );
              const signalPos = interpolate(
                frame,
                [lineDelay + 20, lineDelay + 60],
                [0, 1],
                { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
              );
              const sx = nodeA.x + (nodeB.x - nodeA.x) * signalPos;
              const sy = nodeA.y + (nodeB.y - nodeA.y) * signalPos;
              return (
                <g key={`conn-${i}-${j}`}>
                  <line
                    x1={nodeA.x}
                    y1={nodeA.y}
                    x2={nodeB.x}
                    y2={nodeB.y}
                    stroke="#f43f5e"
                    strokeWidth="2"
                    opacity={lineOp}
                  />
                  {signalPos > 0 && signalPos < 1 && (
                    <circle cx={sx} cy={sy} r="5" fill="#fbbf24" opacity={0.8} />
                  )}
                </g>
              );
            }),
          )}

          {/* Nodes */}
          {p.neuralNodes.map((node, i) => {
            const s = spring({
              frame: frame - (svgStart + i * 12),
              fps,
              config: { damping: 14, stiffness: 120 },
            });
            const pulseR = 24 + 3 * Math.sin(frame * 0.06 + i);
            return (
              <g key={`node-${i}`} opacity={s}>
                <circle
                  cx={node.x}
                  cy={node.y}
                  r={pulseR}
                  fill="#f43f5e20"
                  stroke="#f43f5e"
                  strokeWidth="2.5"
                />
                <circle cx={node.x} cy={node.y} r="8" fill="#f43f5e" />
                <text
                  x={node.x}
                  y={node.y + 50}
                  fill="#fda4af"
                  fontSize="20"
                  fontWeight="700"
                  textAnchor="middle"
                >
                  {node.label}
                </text>
              </g>
            );
          })}
        </svg>

        {/* Enhancement Methods */}
        <div style={{ marginTop: 5 }}>
          <div
            style={{
              fontSize: 26,
              color: '#94a3b8',
              marginBottom: 10,
              letterSpacing: '0.08em',
            }}
          >
            增强可塑性方法
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {p.enhancementMethods.map((em, i) => {
              const s = spring({
                frame: frame - (methodStart + i * 18),
                fps,
                config: { damping: 14, stiffness: 120 },
              });
              return (
                <div
                  key={i}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    background: '#2a0a18',
                    borderRadius: 13,
                    padding: '12px 16px',
                    border: '1.5px solid #be123c20',
                    opacity: s,
                    transform: `translateX(${(1 - s) * 30}px)`,
                  }}
                >
                  <div
                    style={{
                      width: 46,
                      height: 46,
                      borderRadius: 12,
                      background: '#be123c20',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 25,
                      flexShrink: 0,
                    }}
                  >
                    {em.icon}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                      <span style={{ fontSize: 24, fontWeight: 800, color: '#fb7185' }}>
                        {em.name}
                      </span>
                      <span style={{ fontSize: 22, color: '#64748b' }}>{em.duration}</span>
                    </div>
                    <div style={{ fontSize: 20, color: '#94a3b8', marginTop: 2 }}>
                      {em.effect}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ===== RIGHT COLUMN: Brain Facts ===== */}
      <div style={{ position: 'absolute', top: 120, right: 36, width: 500 }}>
        <div
          style={{
            fontSize: 26,
            color: '#94a3b8',
            marginBottom: 10,
            letterSpacing: '0.08em',
          }}
        >
          神经可塑性关键词
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 8,
          }}
        >
          {p.brainFacts.map((fact, i) => {
            const s = spring({
              frame: frame - (factStart + i * 12),
              fps,
              config: { damping: 14, stiffness: 130 },
            });
            return (
              <div
                key={i}
                style={{
                  background: '#2a0a18',
                  borderRadius: 12,
                  padding: '14px 10px',
                  textAlign: 'center',
                  border: '1px solid #be123c30',
                  opacity: s,
                  transform: `scale(${0.8 + 0.2 * s})`,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                  <span style={{ fontSize: 26 }}>{fact.icon}</span>
                  <span style={{ fontSize: 24, fontWeight: 700, color: '#fb7185' }}>
                    {fact.label}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Synaptic Plasticity Diagram */}
        <div style={{ marginTop: 24 }}>
          <div
            style={{
              fontSize: 26,
              color: '#94a3b8',
              marginBottom: 10,
              letterSpacing: '0.08em',
            }}
          >
            突触强化过程
          </div>
          <svg width="480" height="400" viewBox="0 0 320 300">
            {/* Pre-synaptic neuron */}
            <g opacity={interpolate(frame, [svgStart + 40, svgStart + 70], [0, 1], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' })}>
              <circle cx="80" cy="60" r="34" fill="#2a0a18" stroke="#f43f5e" strokeWidth="2.5" />
              <text x="80" y="55" fill="#fb7185" fontSize="16" fontWeight="700" textAnchor="middle">
                突触前
              </text>
              <text x="80" y="75" fill="#fb7185" fontSize="16" fontWeight="700" textAnchor="middle">
                神经元
              </text>
            </g>

            {/* Axon */}
            <line
              x1="80"
              y1="94"
              x2="80"
              y2="140"
              stroke="#f43f5e"
              strokeWidth="2.5"
              opacity={interpolate(frame, [svgStart + 55, svgStart + 75], [0, 0.6], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' })}
            />

            {/* Synapse gap */}
            <g opacity={interpolate(frame, [svgStart + 60, svgStart + 85], [0, 1], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' })}>
              <rect x="45" y="140" width="70" height="35" rx="8" fill="#fbbf2420" stroke="#fbbf24" strokeWidth="1.8" />
              <text x="80" y="163" fill="#fbbf24" fontSize="16" fontWeight="700" textAnchor="middle">
                突触间隙
              </text>
            </g>

            {/* Neurotransmitter dots */}
            {[0, 1, 2, 3, 4].map((j) => {
              const dotY = interpolate(
                frame,
                [svgStart + 70 + j * 5, svgStart + 90 + j * 5],
                [145, 172],
                { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
              );
              const dotOp = interpolate(
                frame,
                [svgStart + 70 + j * 5, svgStart + 75 + j * 5, svgStart + 88 + j * 5, svgStart + 92 + j * 5],
                [0, 0.8, 0.8, 0],
                { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
              );
              return (
                <circle
                  key={`nt-${j}`}
                  cx={55 + j * 12}
                  cy={dotY}
                  r="4"
                  fill="#fbbf24"
                  opacity={dotOp}
                />
              );
            })}

            {/* Post-synaptic neuron */}
            <g opacity={interpolate(frame, [svgStart + 65, svgStart + 90], [0, 1], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' })}>
              <circle cx="80" cy="215" r="34" fill="#2a0a18" stroke="#f43f5e" strokeWidth="2.5" />
              <text x="80" y="210" fill="#fb7185" fontSize="16" fontWeight="700" textAnchor="middle">
                突触后
              </text>
              <text x="80" y="232" fill="#fb7185" fontSize="16" fontWeight="700" textAnchor="middle">
                神经元
              </text>
            </g>

            {/* LTP label */}
            <g opacity={interpolate(frame, [svgStart + 80, svgStart + 100], [0, 1], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' })}>
              <rect x="150" y="125" width="155" height="60" rx="12" fill="#2a0a1880" stroke="#f43f5e40" strokeWidth="1.8" />
              <text x="228" y="153" fill="#fb7185" fontSize="17" fontWeight="700" textAnchor="middle">
                LTP 长时程增强
              </text>
              <text x="228" y="175" fill="#64748b" fontSize="16" textAnchor="middle">
                突触效率持续提升
              </text>
              <line x1="114" y1="155" x2="148" y2="155" stroke="#f43f5e" strokeWidth="1.5" strokeDasharray="4 2" />
            </g>

            {/* Strengthening indicator */}
            <g opacity={networkDraw}>
              <text x="150" y="230" fill="#4ade80" fontSize="16" fontWeight="700">
                反复刺激 → 连接增强
              </text>
              {[0, 1, 2].map((k) => (
                <rect
                  key={`bar-${k}`}
                  x={150 + k * 55}
                  y={240}
                  width={40}
                  height={18 + k * 12}
                  rx="5"
                  fill={`rgba(244,63,94,${0.3 + k * 0.2})`}
                  stroke="#f43f5e40"
                  strokeWidth="1.5"
                />
              ))}
            </g>
          </svg>
        </div>
      </div>

      {/* ===== Stats (bottom bar) ===== */}
      <div
        style={{
          position: 'absolute',
          bottom: 105,
          left: 36,
          right: 36,
          display: 'flex',
          gap: 20,
        }}
      >
        {p.stats.map((st, i) => {
          const stIn = spring({
            frame: frame - (statsStart + i * 14),
            fps,
            config: { damping: 14, stiffness: 100 },
          });
          return (
            <div
              key={i}
              style={{
                flex: 1,
                background: '#2a0a18',
                borderRadius: 14,
                padding: '14px 10px',
                textAlign: 'center',
                border: '1px solid #be123c30',
                opacity: stIn,
                transform: `scale(${0.8 + 0.2 * stIn})`,
              }}
            >
              <div style={{ fontSize: 30, fontWeight: 900, color: '#f43f5e' }}>
                {st.value}
              </div>
              <div style={{ fontSize: 24, color: '#94a3b8', marginTop: 4 }}>
                {st.label}
              </div>
            </div>
          );
        })}
      </div>

      {/* ===== Bottom Quote ===== */}
      <div
        style={{
          position: 'absolute',
          bottom: 20,
          left: 36,
          right: 36,
          background: '#2a0a18',
          borderRadius: 14,
          padding: '14px 22px',
          borderTop: '3px solid #be123c',
          opacity: quoteOpacity,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 24 }}>🧠</span>
          <span style={{ fontSize: 22, color: '#ffe4e6', lineHeight: 1.6 }}>
            {p.bottomQuote}
          </span>
        </div>
      </div>

      {/* Progress bar */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: `${interpolate(frame, [0, 540], [0, 100], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          })}%`,
          height: 4,
          background: 'linear-gradient(90deg, #be123c, #f43f5e, #fecdd3)',
        }}
      />
    </AbsoluteFill>
  );
};
